import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import MiniAppRunner from "@/components/MiniAppRunner";

const PublicMiniAppPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    publicApi
      .get(`/public/mini-apps/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Link is invalid or unshared."));
  }, [token]);

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="card-flat max-w-md text-center">
          <p className="label-eyebrow">Unavailable</p>
          <h1 className="font-serif text-3xl text-[#112018] mt-2">{err}</h1>
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center label-eyebrow">
        Loading…
      </div>
    );

  const s = data.spec || {};
  return (
    <div className="min-h-screen bg-[#F7F6F2] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#1A2F24] flex items-center justify-center">
            <span className="text-[#F7F6F2] font-serif text-xl">L</span>
          </div>
          <div>
            <p className="font-serif text-xl text-[#112018] leading-none">
              {data.firm_name}
            </p>
            <p className="label-eyebrow leading-none mt-1">Mini-App Specification</p>
          </div>
        </div>

        <div className="card-flat" data-testid="public-mini-app">
          <p className="label-eyebrow">{s.category || "Custom Tool"}</p>
          <h1 className="font-serif text-5xl text-[#112018] mt-2">
            {s.title || "Mini-App"}
          </h1>
          <p className="text-[#1A2F24] mt-3 leading-relaxed">{s.summary}</p>

          <div className="mt-8 pt-8 border-t border-[#1A2F24]/12">
            <MiniAppRunner spec={s} />
          </div>

          <p className="label-eyebrow mt-10">Original Description</p>
          <p className="italic text-sm text-[#606C64] mt-2 leading-relaxed">
            {data.description}
          </p>

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {s.inputs?.length > 0 && (
              <div>
                <p className="label-eyebrow">Inputs</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {s.inputs.map((inp, i) => (
                    <li key={i} className="font-mono text-xs">
                      <span className="text-[#1A2F24]">{inp.label}</span>{" "}
                      <span className="text-[#606C64]">· {inp.type}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {s.outputs?.length > 0 && (
              <div>
                <p className="label-eyebrow">Outputs</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {s.outputs.map((o, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium">{o.label}:</span>{" "}
                      <span className="text-[#606C64]">{o.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {s.user_stories?.length > 0 && (
              <div>
                <p className="label-eyebrow">User Stories</p>
                <ul className="mt-2 space-y-1 list-disc pl-5 text-sm">
                  {s.user_stories.map((st, i) => (
                    <li key={i}>{st}</li>
                  ))}
                </ul>
              </div>
            )}
            {s.integration_points?.length > 0 && (
              <div>
                <p className="label-eyebrow">Integrations</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.integration_points.map((p, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 text-xs font-mono bg-[#1A2F24] text-[#F7F6F2]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {s.formula && (
            <div className="mt-8 p-4 bg-[#F7F6F2] border border-[#1A2F24]/12">
              <p className="label-eyebrow">Formula</p>
              <p className="font-mono text-sm mt-2">{s.formula}</p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#606C64] mt-8 label-eyebrow">
          Shared by {data.firm_name}
        </p>
      </div>
    </div>
  );
};

export default PublicMiniAppPage;
